// PDF renderer — walks the resolved sections array and saves an A4 portrait
// document. Pure: no fetching, no toasts; throws on failure (modal owns UX).
import { formatValue, hexToRgb } from './exportUtils';

const GREEN = [16, 185, 129];
const CYAN = [6, 182, 212];
const DARK = [26, 32, 44];
const BODY = [55, 65, 81];
const SECONDARY = [100, 116, 139];
const SUBTLE = [248, 250, 252];
const BORDER = [226, 232, 240];
const WHITE = [255, 255, 255];

const GRANULARITY_LABELS = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  all: 'Full Run',
};

const slug = (str) => String(str)
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

// Mix a color toward white (0..1, 1 = white)
const tint = (rgb, amount) => rgb.map((c) => Math.round(c + (255 - c) * amount));

const fitBox = (width, height, maxW, maxH) => {
  const scale = Math.min(maxW / width, maxH / height);
  return { w: width * scale, h: height * scale };
};

const isNumericType = (type) => type === 'number' || type === 'percent' || type === 'duration';

export const generatePDF = async ({ sections, meta }) => {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentW = pageWidth - margin * 2;

  const primary = hexToRgb(meta.colors.primary);
  const accent = hexToRgb(meta.colors.accent);
  const headTint = tint(primary, 0.88);
  const cardColors = [primary, accent, GREEN, CYAN];

  // Simulated 135deg gradient: thin vertical slices interpolating primary -> accent
  const drawGradient = (x, y, w, h) => {
    const slices = 60;
    const sliceW = w / slices;
    for (let i = 0; i < slices; i++) {
      const t = i / (slices - 1);
      doc.setFillColor(
        Math.round(primary[0] + (accent[0] - primary[0]) * t),
        Math.round(primary[1] + (accent[1] - primary[1]) * t),
        Math.round(primary[2] + (accent[2] - primary[2]) * t)
      );
      doc.rect(x + i * sliceW, y, sliceW + 0.5, h, 'F');
    }
  };

  // ---- Content page chrome (header strip + footer) ----
  const brandedPages = new Set();
  const addPageBranding = () => {
    const docPage = doc.internal.getCurrentPageInfo().pageNumber;
    if (brandedPages.has(docPage)) return;
    brandedPages.add(docPage);

    drawGradient(0, 0, pageWidth, 8);
    doc.setTextColor(...WHITE);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.companyName, margin, 5.5);
    if (meta.logo) {
      const { w, h } = fitBox(meta.logo.width, meta.logo.height, 20, 5.2);
      doc.addImage(meta.logo.dataUrl, 'PNG', pageWidth - margin - w, (8 - h) / 2, w, h);
    }

    doc.setFillColor(...SUBTLE);
    doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.line(0, pageHeight - 12, pageWidth, pageHeight - 12);
    doc.setTextColor(...SECONDARY);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(meta.footerText, margin, pageHeight - 5);
    doc.text(`Page ${docPage - 1}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
    doc.text(meta.dateRangeLabel, pageWidth - margin, pageHeight - 5, { align: 'right' });
  };

  const topY = 18;
  const bottomLimit = pageHeight - 18;
  let y = topY;

  const addContentPage = () => {
    doc.addPage();
    addPageBranding();
    y = topY;
  };

  const ensureSpace = (needed) => {
    if (y + needed > bottomLimit) addContentPage();
  };

  // ---- Cover page ----
  const bandH = pageHeight * 0.45;
  drawGradient(0, 0, pageWidth, bandH);

  if (meta.logo) {
    const { w, h } = fitBox(meta.logo.width, meta.logo.height, 38, 16);
    doc.setFillColor(...WHITE);
    doc.roundedRect(margin, margin, w + 6, h + 6, 2.5, 2.5, 'F');
    doc.addImage(meta.logo.dataUrl, 'PNG', margin + 3, margin + 3, w, h);
  } else {
    doc.setTextColor(...WHITE);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.companyName, margin + 2, margin + 10);
  }

  doc.setTextColor(...WHITE);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text(meta.title, margin + 2, bandH * 0.62, { maxWidth: contentW - 4 });
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(meta.subtitle, margin + 2, bandH * 0.62 + 12, { maxWidth: contentW - 4 });

  const infoCards = [
    { label: 'REPORT PERIOD', value: meta.dateRangeLabel },
    { label: 'GENERATED', value: meta.generatedAt },
  ];
  if (meta.granularity) {
    infoCards.push({ label: 'GRANULARITY', value: GRANULARITY_LABELS[meta.granularity] || meta.granularity });
  }
  const cardGap = 5;
  const infoCardW = (contentW - cardGap * (infoCards.length - 1)) / infoCards.length;
  const infoY = bandH + 14;
  infoCards.forEach((card, i) => {
    const x = margin + i * (infoCardW + cardGap);
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, infoY, infoCardW, 22, 2, 2, 'FD');
    doc.setTextColor(...SECONDARY);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text(card.label, x + 5, infoY + 8);
    doc.setTextColor(...DARK);
    doc.setFontSize(10.5);
    doc.text(card.value, x + 5, infoY + 16, { maxWidth: infoCardW - 10 });
  });

  doc.setTextColor(...SECONDARY);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Powered by StatFalcon', pageWidth / 2, pageHeight - 14, { align: 'center' });

  // ---- Section renderers ----
  const drawSectionHeader = (label, color, minSpace = 60) => {
    ensureSpace(minSpace);
    doc.setFillColor(...color);
    doc.roundedRect(margin, y, 4, 12, 1, 1, 'F');
    doc.setTextColor(...DARK);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(label, margin + 8, y + 9);
    y += 16;
  };

  const drawMetricsGrid = (metrics) => {
    const cards = metrics.slice(0, 8);
    const gap = 4;
    const cardW = (contentW - gap * 2) / 3;
    const cardH = 24;
    cards.forEach((m, i) => {
      const col = i % 3;
      if (col === 0) {
        if (i > 0) y += cardH + gap;
        ensureSpace(cardH + 4);
      }
      const x = margin + col * (cardW + gap);
      const color = cardColors[i % cardColors.length];
      doc.setFillColor(...WHITE);
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.3);
      doc.roundedRect(x, y, cardW, cardH, 1.5, 1.5, 'FD');
      doc.setFillColor(...color);
      doc.rect(x + 0.3, y, cardW - 0.6, 1.1, 'F');
      doc.setTextColor(...SECONDARY);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.text(String(m.label).toUpperCase(), x + 5, y + 8.5);
      doc.setTextColor(...color);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(formatValue(m.value, m.type), x + 5, y + 18);
    });
    y += cardH + 10;
  };

  const MAX_CHART_BUCKETS = 62;

  const drawTrendChart = (rows) => {
    const plotH = 42;
    const chartH = plotH + 16; // legend above + labels below
    ensureSpace(chartH + 6);

    // Sample evenly across the full range instead of truncating to the earliest periods
    const sampled = rows.length > MAX_CHART_BUCKETS;
    const data = sampled
      ? Array.from({ length: MAX_CHART_BUCKETS }, (_, i) => rows[Math.floor((i * rows.length) / MAX_CHART_BUCKETS)])
      : rows;
    const axisW = 13;
    const plotX = margin + axisW;
    const plotW = contentW - axisW;
    const legendY = y;
    const plotTop = y + 6;
    const baseY = plotTop + plotH;
    const maxVal = Math.max(1, ...data.map((r) => Math.max(Number(r.impressions) || 0, Number(r.clicks) || 0)));

    // Legend
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BODY);
    doc.setFillColor(...primary);
    doc.rect(plotX, legendY, 2.5, 2.5, 'F');
    doc.text('Impressions', plotX + 4, legendY + 2.2);
    doc.setFillColor(...accent);
    doc.rect(plotX + 26, legendY, 2.5, 2.5, 'F');
    doc.text('Clicks', plotX + 30, legendY + 2.2);

    // Gridlines + axis value labels
    doc.setFontSize(5.5);
    doc.setTextColor(...SECONDARY);
    for (let i = 0; i <= 4; i++) {
      const gy = baseY - (plotH * i) / 4;
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.15);
      doc.line(plotX, gy, plotX + plotW, gy);
      doc.text(Math.round((maxVal * i) / 4).toLocaleString('en-US'), plotX - 1.5, gy + 1.2, { align: 'right' });
    }
    // Axis baseline
    doc.setDrawColor(...SECONDARY);
    doc.setLineWidth(0.3);
    doc.line(plotX, baseY, plotX + plotW, baseY);

    // Grouped bars
    const n = data.length;
    const group = plotW / n;
    const barW = Math.max(0.5, Math.min(6, group * 0.32));
    data.forEach((r, i) => {
      const cx = plotX + group * i + group / 2;
      const imp = Number(r.impressions) || 0;
      const clk = Number(r.clicks) || 0;
      const impH = imp > 0 ? Math.max(0.4, (imp / maxVal) * plotH) : 0;
      const clkH = clk > 0 ? Math.max(0.4, (clk / maxVal) * plotH) : 0;
      if (impH > 0) {
        doc.setFillColor(...primary);
        doc.rect(cx - barW - 0.3, baseY - impH, barW, impH, 'F');
      }
      if (clkH > 0) {
        doc.setFillColor(...accent);
        doc.rect(cx + 0.3, baseY - clkH, barW, clkH, 'F');
      }
    });

    // Period labels: every nth, skipped entirely when bars are too thin
    if (n <= 40) {
      const step = Math.max(1, Math.ceil(n / 10));
      doc.setFontSize(5.5);
      doc.setTextColor(...SECONDARY);
      data.forEach((r, i) => {
        if (i % step !== 0) return;
        const cx = plotX + group * i + group / 2;
        doc.text(String(r.period), cx, baseY + 3.5, { align: 'center' });
      });
    }

    if (sampled) {
      doc.setFontSize(6);
      doc.setTextColor(...SECONDARY);
      doc.setFont('helvetica', 'italic');
      doc.text(`Showing ${MAX_CHART_BUCKETS} evenly sampled of ${rows.length} periods`, plotX, baseY + 4);
      doc.setFont('helvetica', 'normal');
    }

    y = baseY + 10;
  };

  const ROW_CAP = 50;

  const drawTable = (section) => {
    const { columns, rows } = section;
    const capped = rows.slice(0, ROW_CAP);
    const body = capped.map((row) => columns.map((c) => formatValue(c.get(row), c.type)));
    if (rows.length > ROW_CAP) {
      body.push([{
        content: `+ ${rows.length - ROW_CAP} more rows — see CSV export for full data`,
        colSpan: columns.length,
        styles: { textColor: SECONDARY, fontStyle: 'italic', halign: 'left' },
      }]);
    }

    const columnStyles = {};
    columns.forEach((c, i) => {
      columnStyles[i] = { halign: c.align || (isNumericType(c.type) ? 'right' : 'left') };
    });
    const ctrIndexes = columns.reduce((acc, c, i) => (c.header.startsWith('CTR') ? [...acc, i] : acc), []);

    autoTable(doc, {
      startY: y,
      head: [columns.map((c) => c.header)],
      body,
      theme: 'plain',
      headStyles: { fillColor: headTint, textColor: DARK, fontStyle: 'bold', fontSize: 8, cellPadding: 2.5 },
      bodyStyles: { fontSize: 8, textColor: BODY, cellPadding: 2.2 },
      columnStyles,
      margin: { left: margin, right: margin, top: topY - 2, bottom: 18 },
      didParseCell: (hook) => {
        if (hook.section === 'body' && ctrIndexes.includes(hook.column.index) && hook.row.index < capped.length) {
          hook.cell.styles.fontStyle = 'bold';
          hook.cell.styles.textColor = accent;
        }
      },
      willDrawCell: (hook) => {
        if (hook.section === 'body' && hook.row.index % 2 === 1 && hook.row.index < capped.length) {
          doc.setFillColor(...SUBTLE);
          doc.rect(hook.cell.x, hook.cell.y, hook.cell.width, hook.cell.height, 'F');
        }
      },
      didDrawPage: addPageBranding,
    });

    y = doc.lastAutoTable.finalY + 10;
  };

  // ---- Content flow ----
  addContentPage();
  sections.forEach((section, index) => {
    // Trend needs header (16mm) + chart (58mm plot/legend/labels + 6mm) kept together
    drawSectionHeader(section.label, index % 2 === 0 ? primary : accent, section.key === 'trend' ? 80 : 60);
    if (section.type === 'metrics') {
      drawMetricsGrid(section.metrics);
      return;
    }
    if (section.key === 'trend') {
      drawTrendChart(section.rows);
    }
    drawTable(section);
  });

  const date = new Date().toISOString().split('T')[0];
  doc.save(`${slug(meta.companyName)}_${slug(meta.pageTypeLabel)}_${date}.pdf`);
};
